import { useEffect, useState } from 'react'
import { Download, Share, Plus, X } from 'lucide-react'
import Button from './ui/button'
import { cn } from '../lib/utils'

// Chromium-only event. Other browsers (Safari, Firefox) never fire it.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'pwa-install-dismissed-at'
const INSTALLED_KEY = 'pwa-install-completed'
// Re-show the banner this long after a soft dismissal.
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000

const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false
  const mq = window.matchMedia?.('(display-mode: standalone)').matches
  // iOS Safari exposes navigator.standalone instead of the matchMedia query.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  return Boolean(mq || iosStandalone)
}

const isIos = (): boolean => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const iPadLike = ua.includes('Mac') && 'ontouchend' in document
  return /iPhone|iPad|iPod/.test(ua) || iPadLike
}

const isIosSafari = (): boolean => {
  if (!isIos()) return false
  const ua = navigator.userAgent
  // Exclude in-app browsers and Chrome/Firefox on iOS which can't install PWAs.
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram|Line/.test(ua)
}

const wasRecentlyDismissed = (): boolean => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const at = Number(raw)
    if (!Number.isFinite(at)) return false
    return Date.now() - at < DISMISS_COOLDOWN_MS
  } catch {
    return false
  }
}

const PwaInstallPrompt = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIosHint, setShowIosHint] = useState(false)
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isStandalone()) return
    try {
      if (localStorage.getItem(INSTALLED_KEY) === 'true') return
    } catch {
      // ignore storage failures
    }
    if (wasRecentlyDismissed()) return

    const onBeforeInstall = (event: Event) => {
      event.preventDefault()
      setDeferred(event as BeforeInstallPromptEvent)
      setVisible(true)
    }

    const onInstalled = () => {
      try {
        localStorage.setItem(INSTALLED_KEY, 'true')
      } catch {
        // ignore
      }
      setVisible(false)
      setDeferred(null)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)

    // iOS Safari fallback: no event, so surface the hint after a short delay.
    let iosTimer: number | undefined
    if (isIosSafari()) {
      iosTimer = window.setTimeout(() => {
        setShowIosHint(true)
        setVisible(true)
      }, 4000)
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
      if (iosTimer) window.clearTimeout(iosTimer)
    }
  }, [])

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()))
    } catch {
      // ignore
    }
    setDismissed(true)
    setTimeout(() => setVisible(false), 200)
  }

  const handleInstall = async () => {
    if (!deferred) return
    try {
      await deferred.prompt()
      const choice = await deferred.userChoice
      if (choice.outcome === 'accepted') {
        try {
          localStorage.setItem(INSTALLED_KEY, 'true')
        } catch {
          // ignore
        }
      } else {
        try {
          localStorage.setItem(DISMISS_KEY, String(Date.now()))
        } catch {
          // ignore
        }
      }
    } catch {
      // The prompt can only be used once; swallow errors.
    } finally {
      setDeferred(null)
      setDismissed(true)
      setTimeout(() => setVisible(false), 200)
    }
  }

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label="Install Podcast Generator"
      className={cn(
        'fixed z-50 left-1/2 -translate-x-1/2 bottom-4 sm:bottom-6 w-[calc(100%-1.5rem)] max-w-md',
        'rounded-2xl border border-black/10 bg-background shadow-xl backdrop-blur',
        'transition-all duration-200 ease-out',
        dismissed ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground">Install Podcast Generator</div>
          {showIosHint ? (
            <p className="mt-1 text-sm text-foreground/70">
              Tap{' '}
              <Share className="inline h-4 w-4 align-text-bottom text-brand-600" aria-label="Share" />{' '}
              in Safari, then{' '}
              <span className="inline-flex items-center gap-1 font-medium text-foreground">
                <Plus className="h-3.5 w-3.5" />
                Add to Home Screen
              </span>
              .
            </p>
          ) : (
            <p className="mt-1 text-sm text-foreground/70">
              Add it to your home screen for a faster, full‑screen experience.
            </p>
          )}
          {!showIosHint && (
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" onClick={handleInstall}>
                Install
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                Not now
              </Button>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="ml-1 -mr-1 -mt-1 flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-foreground/50 hover:bg-black/5 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export default PwaInstallPrompt
